import React, { memo, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import Modal from '../modal'
import ModalBody from '../modalBody'
import FormGroup from '../formGroup'
import FormLabel from '../formLabel'
import FormInputGroup from '../formInputGroup'
import FormInputAddon from '../formInputAddon'
import FormInput from '../formInput'
import Button from '../button'
import { translate } from '../../helpers/stringHelpers'
import Alert from '../alert'
import OffersList from '../offersList'
import FeesAlert from '../feesAlert'
import ConfigContext from '../../contexts/configContext'
import trackingEvents from '../../constants/trackingEvents'
import { listings, listingSales, notifications } from '../../actions'
import { trackEvent } from '../../withTracking'
import { toast } from 'react-toastify'
import { formatCurrency } from '../../helpers/currencyHelpers'
import PURCHASE_TYPES from '../../constants/purchaseTypes'

const {
  CATEGORY_LISTING,
  ACTION_OFFER
} = trackingEvents

const PurchaseConfirmationModalOffer = ({
  isOpen,
  listingUuid,
  onClose
}) => {
  const { locale } = useContext(ConfigContext)

  const [isAwaitingResponse, setAwaitingResponse] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const dispatch = useDispatch()
  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const sale = useSelector(state => listingUuid && state.listingSales.sales[listingUuid], shallowEqual)
  const offers = useSelector(state => state.offers.offersByListing[listingUuid]
    ?.map(offerUuid => state.offers.offers[offerUuid]), shallowEqual)
  const autoBidValue = useSelector(state => state.maxBids.maxBidsByListingUuid[listingUuid]?.value || 0)
  const userUuid = useSelector(state => state.users.user.uuid)
  const userId = useSelector(state => state.users.user.id)
  const auctionUuid = listing?.isMarketplace ? 'marketplace' : listing?.auctionUuid
  const isFavourited = useSelector(state => state.lots.favourites[auctionUuid]?.[listingUuid])
  const registrantId = useSelector(state => (userUuid && state.registrations.registrantIdsByUserUuidByAuction[listing?.auctionUuid]?.[userUuid]?.id))
  const maximumOffersPerItem = useSelector(state => state.auctions.configurations[listing?.isMarketplace ? 'marketplace' : auctionUuid]?.maximumOffersPerItem)
  const isComplete = useSelector(state => state.saleStatuses.saleStatuses[listingUuid]?.complete)
  const purchaseTypeId = useSelector(state => state.listingSales.sales[listingUuid]?.purchaseTypeId)

  if (purchaseTypeId !== PURCHASE_TYPES.MAKE_OFFER || !listing) return null
  const { currencyCode, nextBidValue } = sale

  const isAtMaxOffers = offers && (maximumOffersPerItem <= offers.length)
  const minValue = Math.max(nextBidValue, autoBidValue)

  const onChange = (e) => {
    setInputValue(e.target.value)
  }

  const handleOfferConfirm = async (e) => {
    e.preventDefault()
    try {
      setAwaitingResponse(true)
      const offerEvent = await dispatch(listingSales.placeOffer(listingUuid, listing.id, registrantId, userUuid, userId, inputValue))
      trackEvent(CATEGORY_LISTING, ACTION_OFFER, listingUuid, inputValue)
      if (offerEvent) {
        const offerAmount = formatCurrency(locale, currencyCode, inputValue)
        if (offerEvent.offer.rejected) {
          toast.error(translate('JspPublicCodes_JSP_LISTING_OFFER_REJECTED')
            .replace('{0}', offerAmount)
            .replace('{1}', listing.lotNumber))
        } else {
          onClose()
          const successMessage = listing.isMarketplace
            ? translate('JspPublicCodes_JSP_MARKETPLACE_OFFER_PENDING')
              .replace('{0}', offerAmount)
              .replace('{1}', listing.id)
            : translate('JspPublicCodes_JSP_LISTING_OFFER_PENDING')
              .replace('{0}', offerAmount)
              .replace('{1}', listing.lotNumber)

          if (!isFavourited) {
            dispatch(listings.favouriteToggle(auctionUuid, listingUuid, listing.id, userId))
          }
          setInputValue('')
          dispatch(notifications.addAuctionNotification(auctionUuid, listingUuid, 'info', successMessage))
        }
      }
    } catch (err) {
      console.error(err)
      toast.error(err)
    }
    setAwaitingResponse()
  }
  return (
    <Modal onClose={onClose} isOpen={isOpen} id='offerModal'>
        <ModalBody>
          <h4 className='modal__primary-title'>{translate('JspPublicCodes_JSP_BIDDING_MAKE_OFFER')}</h4>
          <h5 className='modal__heading--make-offer'>{listing.title}</h5>
          <p className='modal__content--make-offer' dangerouslySetInnerHTML={{ __html: translate('PageCodes_PAGE_MAKE_OFFER_HELP').replace('{2}', maximumOffersPerItem) }} />
          <OffersList listingUuid={listingUuid} />
          <form onSubmit={handleOfferConfirm}>
            {isAtMaxOffers
              ? (
                <Alert color='warning'>{translate('JspPublicCodes_JSP_LISTING_OFFER_MAX_ATTEMPTS')}</Alert>
                )
              : isComplete
                ? (
                  <Alert color='warning'>{translate('JspPublicCodes_JSP_SALE_ENDED')}</Alert>
                  )
                : (
                  <FormGroup>
                    <FormLabel for='offer__input' className='sr-only'>{translate('JspPublicCodes_JSP_BIDDING_MAKE_OFFER')}</FormLabel>
                    <FormInputGroup>
                      <FormInput
                        min={minValue}
                        onChange={onChange}
                        dataRef='confirmationInput'
                        placeholder={translate('JspPublicCodes_JSP_LISTING_OFFER_INPUT_PLACEHOLDER')}
                        inputMode='numeric'
                        id='offer__input'
                        pattern='[0-9]*'
                        type='text'
                        value={inputValue}
                      />
                      <FormInputAddon>{currencyCode}</FormInputAddon>
                    </FormInputGroup>
                  </FormGroup>
                  )}
            <FeesAlert
              listingUuid={listingUuid}
            />
            <Button
              id='offer__cancel'
              onClick={onClose}
            >
              {translate('JSTextCodes_CANCEL')}
            </Button>
            <Button
              disabled={isAwaitingResponse || isAtMaxOffers || isComplete}
              id='offer__submit'
              type='submit'
              color='primary'
              className='pull-right'
            >
              {translate('JSTextCodes_SEND')}
            </Button>
          </form>
        </ModalBody>
    </Modal>
  )
}

PurchaseConfirmationModalOffer.propTypes = {
  isOpen: PropTypes.bool,
  listingUuid: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.isOpen === b.isOpen &&
  a.listingUuid === b.listingUuid

export default memo(PurchaseConfirmationModalOffer, isEqual)
